// This Modal displays all volunteers allocated in an event

import React, { useEffect, useState, useRef } from "react";
import { Area, Event, Shift, Training, Volunteer } from "../../data/types";
import { MIVolunteerViewMeta, API_BASE_URL, LoadingSpinnerMeta, EU_EVENT_IDS } from "../../data";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { ChevronLeftIcon, ChevronRightIcon } from "../../icons";
import VolunteerTable from "../VolunteerTable"
import { LoadingSpinner } from "../LoadingSpinner";

type Props = {
    isOpen: boolean
    onCancel: () => void
    title: string
    field: string
    value: string
    event?: Event
    area?: Area
    shift?: Shift
    training?: Training
    forTraining?: boolean
    iff?: boolean
    non_iiuk?: boolean,
    checkin?: boolean
    country?: string
};

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

const ITEMS_PER_PAGE = 10

export const VolunteerViewModal = ({
    isOpen,
    onCancel,
    title,
    field,
    value,
    event,
    area,
    shift,
    training,
    forTraining,
    iff,
    non_iiuk,
    checkin,
    country
}: Props) => {

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true)
    const [Volunteers, setVolunteers] = useState<Volunteer[]>([])

    const [loadingPage, setLoadingPage] = useState<number>(1)
    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    })


    const Tag = ({ children }: { children: React.ReactNode }) => (
        <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-600 text-white text-xs font-medium">
            {children}
        </span>
    );


    const buildQueryString = (page: number) => {
        const params = new URLSearchParams()

        params.append('per_page', ITEMS_PER_PAGE.toString())
        params.append('page', page.toString())

        params.append('country', country ?? 'eu');

        if (iff)
            params.append('iff', "1");

        if (non_iiuk)
            params.append('non_iiuk', "1");

        if (checkin)
            params.append('checkin', "1");

        // if training is given
        if (forTraining) {
            if (training) {
                params.append('training_name', training.name.toString());
                params.append('completion_status', (value === "Pending" ? '0' : '1'))
            }
            else {
                params.append('training_name', value);
            }
            // Add event/area/shift filters for training stats
            if (shift)
                params.append('shift_id', shift.id.toString());
            else if (area)
                params.append('area_id', area.id.toString());
            else if (event)
                params.append('event_id', event.id.toString());
            else
                EU_EVENT_IDS.forEach(id => params.append('event_ids[]', id.toString()));
            return params.toString();
        }

        if (shift)
            params.append('shift_id', shift.id.toString());
        else if (area)
            params.append('area_id', area.id.toString());
        else if (event)
            params.append('event_id', event.id.toString());
        else
            EU_EVENT_IDS.forEach(id => params.append('event_ids[]', id.toString()));

        params.append('field', field);
        params.append('value', value);

        return params.toString()
    }

    const fetchVolunteers = async (page: number) => {
        setLoadingPage(page)
        setLoading(true)
        setError(null)

        try {
            const queryString = buildQueryString(page)
            const baseURI = forTraining ? `${API_BASE_URL}/dashboard/stats/group/training` : `${API_BASE_URL}/dashboard/stats/group/volunteer`
            const response = await fetch(`${baseURI}?${queryString}`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const result = await response.json()

            // Handle paginated response structure
            const volunteersData = result.data || []

            // Normalize boolean fields
            const normalized = volunteersData.map((v: any) => ({
                ...v,
                eligible_for_duty: v.eligible_for_duty === 1,
                ismaili: v.ismaili === 1,
                registered_volunteer: v.registered_volunteer === 1,
                multi_faith_family: v.multi_faith_family === 1
            }))

            setVolunteers(normalized)
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching volunteers')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {

        if (!isOpen) return

        const fetchInitialData = async () => {
            // Fetch first page of volunteers

            if (!field || !value) {
                setVolunteers([]);
                setLoading(false);
                return;
            }

            fetchVolunteers(1)
        }

        fetchInitialData()
    }, [isOpen, field, value, event, area, shift, country])

    useEffect(() => {
        if (!isOpen) return;

        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));

        setVolunteers([])
    }, [isOpen]);



    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchVolunteers(page)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            title={title}
            showCloseButton={true}
            maxWidth="5xl"
            footer={
                <div className="flex justify-end">
                    <Button onClick={onCancel} variant="outline" color="neutral">
                        Close
                    </Button>
                </div>
            }
        >

            {loading && (
                <div className="mt-8">
                    <LoadingSpinner meta={LoadingSpinnerMeta} />
                </div>
            )}

            {error && !loading && (
                <div className="m-6 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {!error && !loading && (
                <>
                    {/* Message */}

                    <div className="p-4 mt-4">
                        <p className="text-md text-gray-500 dark:text-gray-400 mb-4">
                            {`Volunteer Count: ${Volunteers?.length ?? 0}`}
                        </p>
                        {/* Table */}
                        <VolunteerTable
                            meta={MIVolunteerViewMeta}
                            data={Volunteers}
                            training={true}
                            status={false}
                            allocation={true}
                            conflicts={false}
                            select={false}
                            disablePagination={true}
                        />
                    </div>
                    {/* Pagination Controls */}
                    {pagination.last_page > 1 && (
                        <div className="flex items-center justify-between mt-4 p-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    onClick={() => handlePageChange(1)}
                                    disabled={pagination.current_page <= 1}
                                    size="sm"
                                >
                                    <ChevronLeftIcon /><ChevronLeftIcon className="w-5 h-5 -ms-[22px]" /> First
                                </Button>

                                <Button
                                    type="button"
                                    onClick={() => handlePageChange(pagination.current_page - 1)}
                                    disabled={pagination.current_page <= 1}
                                    size="sm"
                                >
                                    <ChevronLeftIcon /> Previous
                                </Button>

                                {(() => {
                                    const pageNumbers = []
                                    const maxPagesToShow = 5
                                    let startPage = Math.max(1, pagination.current_page - Math.floor(maxPagesToShow / 2))
                                    let endPage = Math.min(pagination.last_page, startPage + maxPagesToShow - 1)

                                    if (endPage - startPage < maxPagesToShow - 1) {
                                        startPage = Math.max(1, endPage - maxPagesToShow + 1)
                                    }

                                    if (startPage > 1) {
                                        pageNumbers.push(
                                            <Button
                                                key={1}
                                                type="button"
                                                onClick={() => handlePageChange(1)}
                                                size="sm"
                                                variant="icon"
                                            >
                                                1
                                            </Button>
                                        )
                                        if (startPage > 2) {
                                            pageNumbers.push(
                                                <span key="ellipsis1" className="px-2 text-gray-500">...</span>
                                            )
                                        }
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                        pageNumbers.push(
                                            <Button
                                                key={i}
                                                type="button"
                                                onClick={() => handlePageChange(i)}
                                                size="sm"
                                                variant={i === loadingPage ? "solid" : "icon"}
                                                color={i === loadingPage ? "primary" : "neutral"}
                                            >
                                                {i}
                                            </Button>
                                        )
                                    }

                                    if (endPage < pagination.last_page) {
                                        if (endPage < pagination.last_page - 1) {
                                            pageNumbers.push(
                                                <span key="ellipsis2" className="px-2 text-gray-500">...</span>
                                            )
                                        }
                                        pageNumbers.push(
                                            <Button
                                                key={pagination.last_page}
                                                type="button"
                                                onClick={() => handlePageChange(pagination.last_page)}
                                                size="sm"
                                                variant="icon"
                                            >
                                                {pagination.last_page}
                                            </Button>
                                        )
                                    }

                                    return pageNumbers
                                })()}

                                <Button
                                    type="button"
                                    onClick={() => handlePageChange(pagination.current_page + 1)}
                                    disabled={pagination.current_page >= pagination.last_page}
                                    size="sm"
                                >
                                    Next <ChevronRightIcon />
                                </Button>

                                <Button
                                    type="button"
                                    onClick={() => handlePageChange(pagination.last_page)}
                                    disabled={pagination.current_page >= pagination.last_page}
                                    size="sm"
                                >
                                    Last <ChevronRightIcon /><ChevronRightIcon className="w-5 h-5 -ms-[22px]" />
                                </Button>
                            </div>

                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Page {pagination.current_page} of {pagination.last_page}
                            </span>
                        </div>
                    )}
                </>
            )}

        </Modal >
    );
};
